// /parkconnect — UMaps ParkConnect.
//
// One page. It opens on the board — every approved spot, one per row — with a
// "?" for how it works and the two ways in. Both of those swap the board for a
// form and swap back; there is no second route and no scrolling marketing page,
// because the only question most visitors have is "is there a spot in my lot".
//
// Availability is a weekly Monday-to-Friday pattern, not a calendar. Asking
// price is always per month.
//
// "Request this spot" is the parker signup with a listing attached. Nothing is
// matched automatically and no money moves through the site — the submission
// puts both people's details in front of an admin on /parkconnectadmin, who
// approves the pairing and makes the introduction by hand. That limit is stated
// in red on every surface where someone could form the opposite impression; see
// <Disclaimer /> below. It is also why the board carries no contact details —
// `publicListings()` in api/_parkConnectStore.js is the boundary that strips
// them.

import { useCallback, useEffect, useState } from 'react';
import { CSS } from './parkconnect/styles';
import { fetchListings, submitListing, submitRequest } from './parkconnect/api';
import WeekdayPicker from './parkconnect/WeekdayPicker';
import { DocUpload, Field, Note, useLots } from './parkconnect/ui';
import { DAYS, summarize } from './parkconnect/schedule';
import { ArrowRightIcon, CheckCircleIcon, CloseIcon, MarkIcon, ShieldIcon, SpinnerIcon } from './parkconnect/Icons';

/**
 * The one thing on this site that must not be misread. Repeated rather than
 * stated once: someone who lands on a listing, someone filling in a form and
 * someone reading a confirmation are three different people, and any of them
 * could otherwise assume ParkConnect is renting them a stall.
 */
function Disclaimer({ compact }) {
  if (compact) {
    return (
      <p className="pc-redline">
        We only connect the two of you. Everything after that — arranging the handover,
        agreeing a price, paying — is between you and them. ParkConnect is not part of it.
      </p>
    );
  }
  return (
    <Note kind="red">
      <b>We only connect the two of you.</b> ParkConnect passes your details to one other
      person and stops there. All further communication, negotiation and payment is settled
      directly between you — we do not take payment, hold a deposit, or guarantee that a spot
      is available or that either side follows through.
    </Note>
  );
}

/* ── The board ────────────────────────────────────────────────────────────── */

/**
 * The one thing a parker is actually scanning for, made the biggest thing on the
 * row: which days, and between what hours.
 *
 * The five day keys give the shape at a glance — filled means free — and the
 * window sits underneath in the largest type in the list. Most listings have one
 * window across all their days, so that is one bold line; when the days differ
 * they split into a labelled line each rather than collapsing into prose.
 */
function Schedule({ availability }) {
  const groups = summarize(availability);
  if (!groups.length) return null;
  const open = new Set(availability.map(s => s.day));
  const split = groups.length > 1;

  return (
    <div>
      <div className="pc-strip" aria-hidden="true">
        {DAYS.map(({ day, letter }) => (
          <span key={day} className={`pc-strip-day ${open.has(day) ? 'on' : ''}`}>{letter}</span>
        ))}
      </div>
      {groups.map(g => {
        const allDay = g.window === 'all day';
        return (
          <div className="pc-when-row" key={`${g.days}-${g.window}`}>
            {split && <span className="pc-when-days">{g.days}</span>}
            <span className={`pc-when-time ${allDay ? 'all' : ''}`}>
              {allDay ? 'All day' : g.window}
            </span>
          </div>
        );
      })}
    </div>
  );
}

const PAY_LABEL = { monthly: 'Paid monthly', term: 'Paid at start of term' };

function Listing({ listing, onRequest }) {
  const plan = listing.paymentPlan === 'term' ? 'term' : 'monthly';
  const days = listing.availability.map(s => s.day).length;
  return (
    <div className="pc-item">
      <div className="pc-item-main">
        <div className="pc-item-top">
          <span className="pc-lot-tag">Lot {listing.lot}</span>
          {listing.hasReceipt && (
            <ShieldIcon size={13} className="pc-verified" aria-label="Permit receipt checked" />
          )}
          <span className="pc-small" style={{ fontSize: 11.5 }}>
            {days} {days === 1 ? 'day' : 'days'} a week
          </span>
        </div>
        <Schedule availability={listing.availability} />
        {listing.note && <p className="pc-item-note">{listing.note}</p>}
      </div>
      <div className="pc-item-side">
        <div className="pc-item-price">
          ${listing.price}
          <span>per month</span>
        </div>
        <span className={`pc-pay ${plan}`}>{PAY_LABEL[plan]}</span>
        <button type="button" className="pc-btn sm primary" onClick={() => onRequest(listing)}>
          Request this spot
        </button>
      </div>
    </div>
  );
}

function Board({ listings, loading, error, onRequest, onRetry, onFind }) {
  // Weekdays only — the same five the picker and the store accept, so the filter
  // can never ask for a day a listing could not have been filed on.
  const [days, setDays] = useState([]);

  const toggleDay = (day) => setDays(prev => (
    prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
  ));

  // A listing matches if it is free on any day asked for — someone who needs
  // Monday and Wednesday still wants to see the Monday-only spot.
  const shown = days.length === 0
    ? listings
    : listings.filter(l => l.availability.some(s => days.includes(s.day)));

  if (loading) {
    return <div className="pc-empty"><SpinnerIcon size={22} /><p className="pc-small" style={{ marginTop: 10 }}>Loading…</p></div>;
  }

  if (error) {
    return (
      <div className="pc-empty">
        <h2 className="pc-h2">The board would not load</h2>
        <p className="pc-small" style={{ marginBottom: 14 }}>{error}</p>
        <button type="button" className="pc-btn sm" onClick={onRetry}>Try again</button>
      </div>
    );
  }

  return (
    <>
      <div className="pc-bar">
        <div className="pc-filter" role="group" aria-label="Filter by weekday">
          {DAYS.map(({ day, letter, long }) => (
            <button
              type="button" key={day} onClick={() => toggleDay(day)}
              className={`pc-filter-day ${days.includes(day) ? 'on' : ''}`}
              aria-pressed={days.includes(day)} aria-label={long} title={long}
            >
              {letter}
            </button>
          ))}
          {days.length > 0 && (
            <button type="button" className="pc-btn tiny" onClick={() => setDays([])}>Clear</button>
          )}
        </div>
        <span className="pc-spacer" />
        <span className="pc-small">
          {days.length > 0 ? `${shown.length} of ${listings.length}` : `${listings.length} ${listings.length === 1 ? 'spot' : 'spots'}`} listed
        </span>
      </div>

      {shown.length === 0 ? (
        <div className="pc-empty">
          <h2 className="pc-h2">
            {days.length > 0 ? 'Nothing free on those days right now' : 'No spots listed right now'}
          </h2>
          <p className="pc-small" style={{ marginBottom: 14 }}>
            Listings are approved by hand, so the board fills in waves. Sign up as a parker
            and we will email you when a spot opens.
          </p>
          <button type="button" className="pc-btn sm primary" onClick={onFind}>Find your spot</button>
        </div>
      ) : (
        <div className="pc-list">
          {shown.map(listing => <Listing key={listing.id} listing={listing} onRequest={onRequest} />)}
        </div>
      )}

      <div style={{ marginTop: 14 }}><Disclaimer /></div>
    </>
  );
}

/* ── Shared form pieces ───────────────────────────────────────────────────── */

function Done({ title, body, onBack }) {
  return (
    <div className="pc-panel pc-done">
      <CheckCircleIcon size={52} className="pc-check-circle" />
      <h2>{title}</h2>
      <p>{body}</p>
      <div style={{ maxWidth: 460, margin: '0 auto 16px', textAlign: 'left' }}><Disclaimer /></div>
      <button type="button" className="pc-btn sm" onClick={onBack}>Back to the listings</button>
    </div>
  );
}

function Contact({ form, set }) {
  return (
    <>
      <Field label="Your name">
        <input
          className="pc-input" type="text" autoComplete="name" maxLength={120}
          placeholder="First and last name" value={form.name} onChange={e => set('name', e.target.value)}
        />
      </Field>
      <div className="pc-row">
        <Field label="Email" hint="A personal address is fine — this is where we email you.">
          <input
            className="pc-input" type="email" autoComplete="email" maxLength={160}
            placeholder="you@example.com" value={form.email} onChange={e => set('email', e.target.value)}
          />
        </Field>
        <Field label="Phone number">
          <input
            className="pc-input" type="tel" autoComplete="tel" maxLength={32}
            placeholder="204 555 0123" value={form.phone} onChange={e => set('phone', e.target.value)}
          />
        </Field>
      </div>
    </>
  );
}

/**
 * Proof of who is on each end of the introduction, from both sides.
 *
 * A student card is the expected document, but plenty of people sharing a permit
 * are staff, alumni or part-time and have no current card to photograph — so a
 * driver's licence stands in. Which one it is has to be recorded, because an
 * admin checking a name against a card and against a licence is doing two
 * different reads.
 */
function PhotoIdField({ kind, onKind, value, onChange }) {
  const isLicence = kind === 'licence';
  return (
    <Field
      label="Photo ID"
      hint="We check the name on it against the name above. Only we ever see this — it never appears on the board."
    >
      <div className="pc-toggle" style={{ marginBottom: 8 }}>
        <button type="button" className={isLicence ? '' : 'on'} onClick={() => onKind('student')}>Student ID</button>
        <button type="button" className={isLicence ? 'on' : ''} onClick={() => onKind('licence')}>Driver's licence</button>
      </div>
      <DocUpload
        kind="id" value={value} onChange={onChange}
        label={isLicence ? "Attach a photo of your driver's licence" : 'Attach a photo of your student ID'}
        sub={isLicence ? 'No student card? A licence works. JPG, PNG or PDF' : 'JPG, PNG or PDF, up to 6 MB'}
      />
    </Field>
  );
}

function BackLink({ onClick, children }) {
  return (
    <button type="button" className="pc-back" onClick={onClick}>
      <ArrowRightIcon size={14} /> {children}
    </button>
  );
}

/* ── Parker signup ────────────────────────────────────────────────────────── */

const emptyParker = {
  name: '', email: '', phone: '',
  vehicleMake: '', vehicleModel: '', plate: '', note: '',
};

/**
 * One form for both ways a parker arrives: from "Find your spot" (no listing),
 * or from "Request this spot" (with one). Same signup either way — the listing
 * just rides along so the admin sees which pairing to approve.
 */
function ParkerForm({ listing, onBack }) {
  const [form, setForm] = useState(emptyParker);
  // Someone requesting a posted spot almost always wants the hours it is posted
  // for, so the picker opens with them marked rather than empty.
  const [availability, setAvailability] = useState(() => (listing ? listing.availability.map(s => ({ ...s })) : []));
  const [photoId, setPhotoId] = useState(null);
  const [idKind, setIdKind] = useState('student');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = useCallback((k, v) => { setForm(p => ({ ...p, [k]: v })); setErr(''); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!photoId) {
      setErr("Attach a photo of your student ID, or your driver's licence.");
      return;
    }
    setBusy(true); setErr('');
    try {
      await submitRequest({
        ...form,
        plate: form.plate.toUpperCase(),
        availability,
        photoId, photoIdKind: idKind,
        listingId: listing ? listing.id : '',
      });
      setDone(true);
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <Done
        title={listing ? 'Request sent' : "You're signed up"}
        body={listing
          ? 'We have your details and the permit holder\'s. Once we have approved the pairing we will email you both an introduction.'
          : 'You are on the list. When a spot opens that fits, we will email you.'}
        onBack={onBack}
      />
    );
  }

  return (
    <div className="pc-view">
      <BackLink onClick={onBack}>Back to the listings</BackLink>
      <div className="pc-panel">
        <h1 className="pc-h1">{listing ? `Request the spot in lot ${listing.lot}` : 'Find your spot'}</h1>
        <p className="pc-lede" style={{ marginBottom: 14 }}>
          {listing
            ? `$${listing.price} per month, ${listing.paymentPlan === 'term' ? 'paid in full at the start of term' : 'paid monthly'}. Sign up as a parker and we will approve the pairing and introduce you.`
            : 'Sign up as a parker. We will email you when a spot opens that fits.'}
        </p>

        <Disclaimer />
        <div style={{ height: 16 }} />

        <form onSubmit={submit} noValidate>
          <Contact form={form} set={set} />

          <PhotoIdField kind={idKind} onKind={setIdKind} value={photoId} onChange={setPhotoId} />

          <fieldset className="pc-fieldset">
            <legend>Your vehicle</legend>
            <p className="pc-hint" style={{ marginTop: 0, marginBottom: 12 }}>
              The lot is patrolled, so the permit holder needs to know what will be in their stall.
            </p>
            <div className="pc-row three">
              <Field label="Make">
                <input
                  className="pc-input" type="text" maxLength={60} placeholder="Honda"
                  value={form.vehicleMake} onChange={e => set('vehicleMake', e.target.value)}
                />
              </Field>
              <Field label="Model">
                <input
                  className="pc-input" type="text" maxLength={60} placeholder="Civic"
                  value={form.vehicleModel} onChange={e => set('vehicleModel', e.target.value)}
                />
              </Field>
              <Field label="Licence plate">
                <input
                  className="pc-input plate" type="text" maxLength={12} placeholder="ABC 123"
                  autoCapitalize="characters" autoCorrect="off" spellCheck="false"
                  value={form.plate} onChange={e => set('plate', e.target.value.toUpperCase())}
                />
              </Field>
            </div>
          </fieldset>

          <Field
            label={listing ? 'Which of these days do you need?' : 'When do you need parking?'}
            optional
            hint={listing
              ? 'Pre-filled with the days this spot is free. Trim it to what you actually need, or leave it as is.'
              : 'Leave this blank and we will tell you about anything that opens. Fill it in and we will only email you when a spot matches these days.'}
          >
            <WeekdayPicker value={availability} onChange={setAvailability} />
          </Field>

          <Field label="Anything else?" optional>
            <textarea
              className="pc-textarea" maxLength={600}
              placeholder="I only need it until my 2:30 lab lets out."
              value={form.note} onChange={e => set('note', e.target.value)}
            />
          </Field>

          {err && <Note kind="bad" icon={<CloseIcon size={15} />}>{err}</Note>}

          <button type="submit" className="pc-btn primary block" disabled={busy} style={{ marginTop: 14 }}>
            {busy ? <><SpinnerIcon size={15} /> Sending…</> : listing ? 'Send request' : 'Sign up as a parker'}
          </button>
          <p className="pc-hint" style={{ textAlign: 'center' }}>
            Your details go to us, not onto the board.
          </p>
        </form>
      </div>
    </div>
  );
}

/* ── Permit holder listing ────────────────────────────────────────────────── */

const emptyHolder = {
  name: '', email: '', phone: '', lot: '', price: '', paymentPlan: 'monthly',
  scheduleNote: '', note: '',
};

function HolderForm({ onBack, onListed }) {
  const lots = useLots();
  const [form, setForm] = useState(emptyHolder);
  const [availability, setAvailability] = useState([]);
  const [receipt, setReceipt] = useState(null);
  const [photoId, setPhotoId] = useState(null);
  const [idKind, setIdKind] = useState('student');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = useCallback((k, v) => { setForm(p => ({ ...p, [k]: v })); setErr(''); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!photoId) {
      setErr("Attach a photo of your student ID, or your driver's licence.");
      return;
    }
    if (!receipt) {
      setErr('Attach your permit receipt — it is how we confirm the spot is yours to offer.');
      return;
    }
    setBusy(true); setErr('');
    try {
      await submitListing({
        ...form,
        price: form.price === '' ? '' : Number(form.price),
        availability,
        photoId, photoIdKind: idKind,
        permitReceipt: receipt,
      });
      setDone(true);
      onListed();
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <Done
        title="Your spot is in the queue"
        body="We check every listing before it goes up, usually the same day. You will get an email once it is live, and again when a parker asks for it."
        onBack={onBack}
      />
    );
  }

  return (
    <div className="pc-view">
      <BackLink onClick={onBack}>Back to the listings</BackLink>
      <div className="pc-panel">
        <h1 className="pc-h1">List your spot</h1>
        <p className="pc-lede" style={{ marginBottom: 14 }}>
          Post the weekdays you are not using your permit. We confirm it is yours,
          put it on the board, and bring you a parker.
        </p>

        <Disclaimer />
        <div style={{ height: 16 }} />

        <form onSubmit={submit} noValidate>
          <Note kind="info" icon={<ShieldIcon size={15} />}>
            Your name, email, phone, photo ID and permit receipt never appear on the board —
            only the lot, the hours and your price. We pass your name and contact details to
            one parker, once, when we make the introduction. Your documents we keep to
            ourselves.
          </Note>
          <div style={{ height: 16 }} />

          <Contact form={form} set={set} />

          <PhotoIdField kind={idKind} onKind={setIdKind} value={photoId} onChange={setPhotoId} />

          <Field label="Parking lot" hint="The lot your permit is issued for.">
            <select className="pc-select" value={form.lot} onChange={e => set('lot', e.target.value)}>
              <option value="">Pick a lot…</option>
              {lots.map(l => <option key={l} value={l}>Lot {l}</option>)}
            </select>
          </Field>

          <Field
            label="When is the spot free?"
            hint="Tap the weekdays you are away. Each one is the whole day unless you set hours for it."
          >
            <WeekdayPicker value={availability} onChange={setAvailability} />
          </Field>

          <Field label="Asking price">
            <div className="pc-price-input">
              <span className="pc-price-prefix">$</span>
              <input
                className="pc-input" type="number" min="0" max="9999" step="1" inputMode="decimal"
                placeholder="90" value={form.price} onChange={e => set('price', e.target.value)}
              />
              <span className="pc-price-suffix">per month</span>
            </div>
          </Field>

          {/* Always a monthly rate, so listings stay comparable — this is only
              about when the holder wants the money. */}
          <Field label="How do you want to be paid?" hint="Shown on your listing, so a parker knows before they ask.">
            <div className="pc-toggle">
              <button
                type="button" className={form.paymentPlan === 'term' ? '' : 'on'}
                onClick={() => set('paymentPlan', 'monthly')}
              >
                Monthly
              </button>
              <button
                type="button" className={form.paymentPlan === 'term' ? 'on' : ''}
                onClick={() => set('paymentPlan', 'term')}
              >
                All at once, at the start of term
              </button>
            </div>
            <Disclaimer compact />
          </Field>

          <Field label="Permit receipt" hint="A photo or the PDF from Parking Services. Only we see it.">
            <DocUpload
              value={receipt} onChange={setReceipt}
              label="Attach your permit receipt" sub="JPG, PNG or PDF, up to 6 MB"
            />
          </Field>

          <Field label="Anything else about the hours?" optional>
            <textarea
              className="pc-textarea" maxLength={600}
              placeholder="Gone by 11:30 most Tuesdays."
              value={form.scheduleNote} onChange={e => set('scheduleNote', e.target.value)}
            />
          </Field>

          <Field label="A note for the parker" optional hint="Shown on the board, so keep it about the spot.">
            <textarea
              className="pc-textarea" maxLength={600}
              placeholder="Second row in from the Dafoe entrance."
              value={form.note} onChange={e => set('note', e.target.value)}
            />
          </Field>

          {err && <Note kind="bad" icon={<CloseIcon size={15} />}>{err}</Note>}

          <button type="submit" className="pc-btn primary block" disabled={busy} style={{ marginTop: 14 }}>
            {busy ? <><SpinnerIcon size={15} /> Sending…</> : 'Submit for review'}
          </button>
          <p className="pc-hint" style={{ textAlign: 'center' }}>
            Nothing goes on the board until we have looked at it.
          </p>
        </form>
      </div>
    </div>
  );
}

/* ── Help ─────────────────────────────────────────────────────────────────── */

function HelpModal({ onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="pc-backdrop" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="pc-modal" role="dialog" aria-modal="true" aria-label="How ParkConnect works">
        <div className="pc-modal-head">
          <strong>How this works</strong>
          <button type="button" className="pc-modal-x" onClick={onClose} aria-label="Close"><CloseIcon size={15} /></button>
        </div>
        <div className="pc-modal-body">
          <ol className="pc-steps">
            <li>
              <b>A permit holder lists a spot.</b> They post the weekdays they are not on
              campus, a monthly asking price, whether they want paying monthly or up front at
              the start of term, photo ID, and their permit receipt so we can confirm the
              stall is theirs to offer.
            </li>
            <li>
              <b>A parker signs up.</b> Name, contact details, photo ID, vehicle, and
              optionally the weekdays they need — either against a spot on the board, or on
              file to be emailed when one opens.
            </li>
            <li>
              <b>We approve the pairing and introduce you.</b> Nothing is matched
              automatically. We read both sides, then email the two of you together.
            </li>
            <li>
              <b>That is where we stop.</b> The handover, the terms and the money are yours
              to sort out with each other.
            </li>
          </ol>
          <div style={{ height: 14 }} />
          <Disclaimer />
          <p className="pc-small" style={{ marginTop: 12 }}>
            Check your permit's terms before sharing it.
          </p>
        </div>
      </div>
    </div>
  );
}

/* ── Page ─────────────────────────────────────────────────────────────────── */

export default function ParkConnectPage() {
  // 'board' | 'parker' | 'holder'. A view, not a route — the whole feature is
  // one page and the back button out of a form is a button, not history.
  const [view, setView] = useState('board');
  const [requesting, setRequesting] = useState(null);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [help, setHelp] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      setListings(await fetchListings());
    } catch (e) {
      setError(e.message || 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toBoard = useCallback(() => {
    setView('board');
    setRequesting(null);
    window.scrollTo({ top: 0 });
  }, []);

  const open = useCallback((next, listing = null) => {
    setRequesting(listing);
    setView(next);
    window.scrollTo({ top: 0 });
  }, []);

  return (
    <div className="pc-root">
      <style>{CSS}</style>

      <header className="pc-head">
        <div className="pc-wrap pc-head-in">
          <a className="pc-brand" href="/umanitoba">
            <MarkIcon size={22} className="pc-mark" />
            UMaps <span className="pc-brand-sub">ParkConnect</span>
          </a>
          <button type="button" className="pc-help" onClick={() => setHelp(true)} aria-label="How this works">?</button>
          <div className="pc-head-actions">
            <button type="button" className="pc-btn sm primary" onClick={() => open('parker')}>Find your spot</button>
            <button type="button" className="pc-btn sm" onClick={() => open('holder')}>List your spot</button>
          </div>
        </div>
      </header>

      <main className="pc-wrap pc-main">
        {view === 'board' && (
          <div className="pc-view">
            <Board
              listings={listings} loading={loading} error={error} onRetry={load}
              onRequest={l => open('parker', l)} onFind={() => open('parker')}
            />
          </div>
        )}
        {view === 'parker' && <ParkerForm listing={requesting} onBack={toBoard} />}
        {view === 'holder' && <HolderForm onBack={toBoard} onListed={load} />}
      </main>

      <footer className="pc-foot">
        <div className="pc-wrap pc-foot-in">
          <span className="pc-small">UMaps ParkConnect</span>
          <span className="pc-spacer" />
          <a href="/umanitoba">Campus map</a>
          <a href="/parking">Parking rates</a>
          <button type="button" className="pc-btn link" onClick={() => setHelp(true)}>How it works</button>
        </div>
      </footer>

      {help && <HelpModal onClose={() => setHelp(false)} />}
    </div>
  );
}
